import React from "react";
import {
  Currency,
  ImpactShopProductImage,
  ImpactShopProductUnitType,
  penceToDisplayPrice,
  ProductType,
  Status,
  isImpactFundProductType,
  currencyInSmallestUnit,
} from "@ecologi/shared";
import Image from "next/legacy/image";
import {
  Body,
  Description,
  ImageContainer,
  CentredBadge,
  Wrapper,
  Title,
  Cost,
  TagsContainer,
} from "./ImpactShopProductCard.styles";
import { buildDataUrlFromBase64 } from "../utils";
import { StatusTag } from "../StatusTag";

export type ImpactShopProductCard = {
  badgePath?: string;
  currency: Currency;
  description: string;
  image: ImpactShopProductImage;
  isOutOfStock: boolean;
  price: number;
  tags: Status[];
  title: string;
  unit: ImpactShopProductUnitType | null;
  minQuantity: number;
  productType: ProductType;
};

export type ImpactShopProductCardProps = {
  onCardClick: () => void;
} & ImpactShopProductCard;

const UnitTypeVisualTextMapper: Record<ImpactShopProductUnitType, JSX.Element> =
  {
    ["tree"]: <>tree</>,
    ["tonne CO2e"]: (
      <>
        tonne CO<sub>2</sub>e
      </>
    ),
    ["kg"]: <>kg</>,
    ["m2"]: (
      <>
        m<sup>2</sup>
      </>
    ),
  };

export const ImpactShopProductCard = ({
  badgePath,
  currency,
  description,
  image,
  isOutOfStock,
  onCardClick,
  price,
  tags,
  title,
  unit,
  minQuantity,
  productType,
}: ImpactShopProductCardProps) => {
  return (
    <Wrapper
      data-testid={`impactShopProductCard.${productType}`}
      disabled={isOutOfStock}
      aria-disabled={isOutOfStock}
      onClick={() => !isOutOfStock && onCardClick()}
    >
      <ImageContainer>
        <Image
          alt={`Example ${title} project`}
          blurDataURL={buildDataUrlFromBase64(image.placeholder)}
          layout="fill"
          objectFit="cover"
          placeholder="blur"
          sizes="(max-width: 767px) 100vw, (max-width: 900px) 75vw, (max-width: 1231px) 40vw, (max-width: 1602px) 30vw, 20vw"
          src={image.url}
        />
        {badgePath && (
          <CentredBadge>
            <Image src={badgePath} alt={`${title} badge`} layout="fill" />
          </CentredBadge>
        )}
      </ImageContainer>

      <Body>
        <TagsContainer>
          {tags.map((tag) => (
            <StatusTag
              key={tag.label}
              label={tag.label}
              variant={tag.variant}
            />
          ))}
        </TagsContainer>

        <Title size={5} as="h3">
          {title}
        </Title>
        <div>
          <Cost use="body3">
            {isImpactFundProductType(productType) ? (
              `From ${penceToDisplayPrice(
                currencyInSmallestUnit(minQuantity),
                currency
              )}`
            ) : (
              <>
                {penceToDisplayPrice(price, currency)}
                {unit && "/"}
                {unit && UnitTypeVisualTextMapper[unit]}
              </>
            )}
          </Cost>
          <Description use="dashboardMicroCopyRegular">
            {description}
          </Description>
        </div>
      </Body>
    </Wrapper>
  );
};
